import React, { useState } from 'react';

const STEPS = [
  {
    icon: '📦',
    title: 'Receive Orders',
    desc: 'New orders appear instantly in the Orders tab. Your dashboard plays a ringtone and shows a banner. Switch to "Pending" filter to see new orders first.',
    tip: 'Keep this tab open on your pharmacy computer for instant alerts.',
  },
  {
    icon: '💊',
    title: 'Prepare Medicines',
    desc: 'Click the status dropdown on any order and change it to "Preparing". This tells the customer their order is being packed. The dosage details show exactly how much to dispense.',
    tip: 'Always check patient allergies shown on each order card.',
  },
  {
    icon: '🛵',
    title: 'Dispatch to Rider',
    desc: 'Once packed, change status to "Dispatched". A rider will pick up the medicines. You can see the rider name once they accept.',
    tip: 'Print the receipt for the rider to carry with the delivery.',
  },
  {
    icon: '📋',
    title: 'Manage Inventory',
    desc: 'Go to the Inventory tab to add medicines. Set your price — customers pay 10% more. Import hundreds of medicines at once using the Excel import button.',
    tip: 'Use the AI category detection — just enter the medicine name and the category fills automatically.',
  },
  {
    icon: '📈',
    title: 'Track Sales',
    desc: 'The Sales tab shows your revenue, top medicines, and order history. Export to Excel for accounting. Switch between Today, Week, and Month views.',
    tip: 'Your earnings show what you receive after PharmDev\'s 10% commission.',
  },
  {
    icon: '💰',
    title: 'Get Paid',
    desc: 'Set up your MTN MoMo or bank account in the Earnings tab. PharmDev pays daily for MoMo and weekly for bank transfers after orders are delivered.',
    tip: 'You receive 100% of your set medicine price. The 10% commission is added on top for the customer.',
  },
  {
    icon: '🕐',
    title: 'Set Working Hours',
    desc: 'Go to the Hours tab to set your opening and closing times for each day. Customers see live open/closed status on the map based on your schedule.',
    tip: 'Use "Copy Mon to all weekdays" to set the same hours in one click.',
  },
  {
    icon: '📋',
    title: 'Handle Prescriptions',
    desc: 'Customers can upload prescription photos. Go to the Prescriptions tab to review. AI reads the prescription automatically. Approve or reject with one click.',
    tip: 'Always verify the prescription details before dispensing controlled medicines.',
  },
];

export default function UserGuide({ onClose }) {
  const [step, setStep] = useState(0);

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.7)', zIndex: 99999,
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
    }}>
      <div style={{
        backgroundColor: '#FFF', borderRadius: 20, padding: 40,
        maxWidth: 520, width: '100%',
        boxShadow: '0 24px 80px rgba(0,0,0,0.3)',
      }}>

        {/* HEADER */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 28 }}>
          <div>
            <p style={{ fontSize: 12, color: '#1D9E75', fontWeight: 700, margin: '0 0 4px', textTransform: 'uppercase', letterSpacing: 1 }}>
              Getting Started Guide
            </p>
            <h2 style={{ fontSize: 22, fontWeight: 800, margin: 0, color: '#1A1A1A' }}>
              Welcome to PharmDev 💊
            </h2>
          </div>
          <button
            style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 22, color: '#888', padding: 4 }}
            onClick={onClose}
          >
            ✕
          </button>
        </div>

        {/* PROGRESS */}
        <div style={{ display: 'flex', gap: 4, marginBottom: 28 }}>
          {STEPS.map((_, i) => (
            <div
              key={i}
              style={{
                flex: 1, height: 4, borderRadius: 2, cursor: 'pointer',
                backgroundColor: i <= step ? '#1D9E75' : '#E5E7EB',
                transition: 'background-color 0.2s',
              }}
              onClick={() => setStep(i)}
            />
          ))}
        </div>

        {/* STEP CONTENT */}
        <div style={{ marginBottom: 28 }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>{STEPS[step].icon}</div>
          <h3 style={{ fontSize: 20, fontWeight: 700, marginBottom: 10, color: '#1A1A1A' }}>
            {step + 1}. {STEPS[step].title}
          </h3>
          <p style={{ fontSize: 14, color: '#555', lineHeight: 1.7, marginBottom: 14 }}>
            {STEPS[step].desc}
          </p>
          <div style={{ backgroundColor: '#E8F8F2', borderRadius: 10, padding: 14, border: '1px solid #B2DFD0' }}>
            <p style={{ fontSize: 13, color: '#1D6B52', margin: 0, lineHeight: 1.6 }}>
              💡 <strong>Tip:</strong> {STEPS[step].tip}
            </p>
          </div>
        </div>

        {/* NAVIGATION */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <button
            style={{
              padding: '10px 20px', border: '1px solid #E5E7EB',
              borderRadius: 10, cursor: step === 0 ? 'not-allowed' : 'pointer',
              fontSize: 14, backgroundColor: '#FFF', color: '#555',
              opacity: step === 0 ? 0.4 : 1,
            }}
            onClick={() => step > 0 && setStep(s => s - 1)}
            disabled={step === 0}
          >
            ← Previous
          </button>

          <span style={{ fontSize: 13, color: '#888' }}>
            {step + 1} of {STEPS.length}
          </span>

          {step < STEPS.length - 1 ? (
            <button
              style={{
                padding: '10px 20px', backgroundColor: '#1D9E75',
                color: '#FFF', border: 'none', borderRadius: 10,
                cursor: 'pointer', fontSize: 14, fontWeight: 700,
              }}
              onClick={() => setStep(s => s + 1)}
            >
              Next →
            </button>
          ) : (
            <button
              style={{
                padding: '10px 24px', backgroundColor: '#1D9E75',
                color: '#FFF', border: 'none', borderRadius: 10,
                cursor: 'pointer', fontSize: 14, fontWeight: 700,
              }}
              onClick={onClose}
            >
              Start Using Dashboard ✓
            </button>
          )}
        </div>

        {/* SKIP */}
        {step < STEPS.length - 1 && (
          <div style={{ textAlign: 'center', marginTop: 16 }}>
            <button
              style={{ background: 'none', border: 'none', color: '#888', cursor: 'pointer', fontSize: 13 }}
              onClick={onClose}
            >
              Skip guide — I'll explore myself
            </button>
          </div>
        )}
      </div>
    </div>
  );
}